// Platform (superadmin only): correos tipo. Editable e-mails for leads of
// tallerza.cl and workshops (Bienvenida and the playbook's B, C and D, plus
// the superadmin's own), the links their buttons use, and sending one.
// Templates and links are written only through callables (firestore.rules).
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Mail, Plus, Send } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { useEnlacesCorreo, usePlantillasCorreo } from '@/datos/consultas';
import { exigir } from '@/lib/guardias';
import { permisos } from '@/lib/sesion';
import { combinarPlantillas, type PlantillaVista } from '@/plataforma/correos';
import { DialogoEnviarCorreo, type InicialEnvio } from '@/plataforma/DialogoEnviarCorreo';
import { EditorPlantilla } from '@/plataforma/EditorPlantilla';
import { FormEnlaces } from '@/plataforma/FormEnlaces';
import { ErrorCarga, Esqueleto, Insignia, Tarjeta, TituloTarjeta } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { cn } from '@/ui/cn';
import { Contenedor } from '@/ui/pagina';

/** `nueva` = the editor of a new template. */
const busqueda = z.object({
  plantilla: z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,128}$/)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute('/_app/plataforma/correos')({
  validateSearch: busqueda,
  beforeLoad: exigir(permisos.plataforma),
  component: Pagina,
});

const NUEVA = 'nueva';

function Pagina() {
  const q = usePlantillasCorreo();
  const enlaces = useEnlacesCorreo();
  const { plantilla: elegidaUrl } = Route.useSearch();
  const navegar = useNavigate({ from: Route.fullPath });
  const [envio, setEnvio] = useState<{ abierto: boolean; n: number; inicial: InicialEnvio }>({
    abierto: false,
    n: 0,
    inicial: {},
  });
  const editorRef = useRef<HTMLDivElement>(null);
  const [desplazar, setDesplazar] = useState(0);

  const plantillas = useMemo(() => combinarPlantillas(q.data ?? []), [q.data]);
  const vigentes = plantillas.filter((p) => !p.archivada);
  const archivadas = plantillas.filter((p) => p.archivada);
  const elegidaId = elegidaUrl ?? 'bienvenida';
  const elegida = elegidaId === NUEVA ? null : plantillas.find((p) => p.id === elegidaId);

  // On phones the editor is below the list: take the user to it.
  useEffect(() => {
    if (!desplazar) return;
    const el = editorRef.current;
    if (el && el.getBoundingClientRect().top > window.innerHeight * 0.6)
      el.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [desplazar]);

  function elegir(id: string) {
    void navegar({ search: { plantilla: id }, replace: true });
    setDesplazar((n) => n + 1);
  }

  function abrirEnvio(inicial: InicialEnvio = {}) {
    setEnvio((x) => ({ abierto: true, n: x.n + 1, inicial }));
  }

  const cargando = !q.data || !enlaces.data;

  return (
    <Contenedor>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="titulo text-[24px] leading-tight">Correos</h1>
          <p className="text-[15px] text-text-2">
            Plantillas para escribir a los contactos del sitio y a los talleres
          </p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <Boton onClick={() => elegir(NUEVA)}>
            <Plus /> Nueva plantilla
          </Boton>
          <Boton variante="primario" onClick={() => abrirEnvio()}>
            <Send /> Enviar correo
          </Boton>
        </div>
      </div>

      {q.isError || enlaces.isError ? (
        <ErrorCarga error={q.error ?? enlaces.error} />
      ) : cargando ? (
        <div className="grid gap-5 lg:grid-cols-[300px_minmax(0,1fr)]" aria-busy="true">
          <span className="sr-only">Cargando plantillas…</span>
          <Tarjeta className="flex flex-col gap-3 p-4">
            <Esqueleto className="h-14" />
            <Esqueleto className="h-14" />
            <Esqueleto className="h-14" />
          </Tarjeta>
          <Tarjeta className="flex flex-col gap-3 p-5">
            <Esqueleto className="h-10" />
            <Esqueleto className="h-64" />
          </Tarjeta>
        </div>
      ) : (
        <>
          <div className="grid items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
            <Tarjeta className="flex flex-col gap-1 p-2" aria-labelledby="titulo-plantillas">
              <h2
                id="titulo-plantillas"
                className="px-3 pt-2 pb-1 text-[12px] font-bold tracking-[0.06em] text-text-2 uppercase"
              >
                Plantillas
              </h2>
              <ListaPlantillas plantillas={vigentes} elegidaId={elegidaId} onElegir={elegir} />
              {archivadas.length > 0 && (
                <details
                  className="group mt-1 border-t border-border pt-1"
                  open={elegida?.archivada === true || undefined}
                >
                  <summary className="cursor-pointer rounded-[10px] px-3 py-2.5 text-[13px] font-bold text-text-2 select-none hover:bg-surface-muted">
                    Archivadas ({archivadas.length})
                  </summary>
                  <ListaPlantillas
                    plantillas={archivadas}
                    elegidaId={elegidaId}
                    onElegir={elegir}
                  />
                </details>
              )}
            </Tarjeta>

            <Tarjeta className="min-w-0 scroll-mt-4 p-5 sm:p-6" ref={editorRef}>
              {elegidaId !== NUEVA && !elegida ? (
                <p className="text-text-2">
                  Esa plantilla no existe.{' '}
                  <Boton variante="enlace" onClick={() => elegir('bienvenida')}>
                    Ver la Bienvenida
                  </Boton>
                </p>
              ) : (
                <EditorPlantilla
                  key={elegidaId}
                  plantilla={elegida ?? null}
                  enlaces={enlaces.data}
                  onGuardada={(id) => {
                    if (id !== elegidaId) elegir(id);
                  }}
                  onEnviar={(p) => abrirEnvio(p)}
                />
              )}
            </Tarjeta>
          </div>

          <Tarjeta id="enlaces" className="flex scroll-mt-4 flex-col gap-3 p-5 sm:p-6">
            <TituloTarjeta>Enlaces</TituloTarjeta>
            <p className="max-w-[72ch] text-[14px] text-text-2">
              A dónde llevan los botones de la guía, el video y el manual. Se cambian aquí, sin
              tocar las plantillas; uno vacío bloquea el envío de las plantillas que lo usan.
            </p>
            <FormEnlaces enlaces={enlaces.data} />
          </Tarjeta>
        </>
      )}

      <DialogoEnviarCorreo
        key={envio.n}
        abierto={envio.abierto}
        inicial={envio.inicial}
        onCambio={(v) => setEnvio((x) => ({ ...x, abierto: v }))}
      />
    </Contenedor>
  );
}

function ListaPlantillas({
  plantillas,
  elegidaId,
  onElegir,
}: {
  plantillas: PlantillaVista[];
  elegidaId: string;
  onElegir: (id: string) => void;
}) {
  return (
    <ul className="flex flex-col gap-0.5">
      {plantillas.map((p) => {
        const actual = p.id === elegidaId;
        return (
          <li key={p.id}>
            <button
              type="button"
              aria-current={actual ? 'true' : undefined}
              onClick={() => onElegir(p.id)}
              className={cn(
                'flex w-full items-start gap-3 rounded-[10px] px-3 py-2.5 text-left transition-colors duration-150',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-sel',
                actual ? 'bg-primary-soft' : 'hover:bg-surface-muted',
              )}
            >
              <Mail
                className={cn(
                  'mt-0.5 size-4 flex-none',
                  actual ? 'text-primary-text' : 'text-text-2',
                )}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    'block truncate text-[14px] font-bold',
                    actual ? 'text-primary-text' : 'text-text',
                  )}
                >
                  {p.nombre}
                </span>
                <span className="block truncate text-[13px] text-text-2">{p.asunto}</span>
              </span>
              {!p.base ? (
                <Insignia tono="neutro" className="mt-0.5">
                  Propia
                </Insignia>
              ) : p.editada ? (
                <Insignia tono="acento" className="mt-0.5">
                  Editada
                </Insignia>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
